import { useEffect, useState, useMemo } from 'react';
import * as XLSX from 'xlsx';
import api from '../../utils/api';
import { useCurrency } from '../../contexts/CurrencyContext';

/**
 * Creditors Aging Report — supplier-side counterpart to Debtors Aging.
 *
 * Buckets each outstanding (approved or received) PO by the age of the
 * `po_date` and aggregates per supplier-and-currency:
 *   • Pending Bills (count)
 *   • Pending Total
 *   • <30 / 30-60 / 60-120 / >120 days
 *
 * Per-currency totals (not a single mixed-currency grand total) to avoid
 * misleading USD+EUR+BHD addition.
 *
 * Lives as the 3rd tab inside the Supplier Payments page (Payments ·
 * Supplier Ledger · Creditors Aging).
 */
const CreditorsAgingTab = () => {
  const { companySettings } = useCurrency();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [asOf, setAsOf] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const url = asOf
          ? `/supplier-payments/reports/creditors-aging?as_of=${asOf}`
          : '/supplier-payments/reports/creditors-aging';
        const res = await api.get(url);
        if (!cancelled) setData(res.data);
      } catch {
        if (!cancelled) setData({ rows: [], totals_by_currency: [], as_of: '' });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [asOf]);

  const visibleRows = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    if (!q) return data.rows;
    return data.rows.filter((r) => (r.supplier_name || '').toLowerCase().includes(q));
  }, [data, search]);

  const fmt = (n) => (parseFloat(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 3 });

  /* ── Excel export ───────────────────────────────────────────────── */
  const exportExcel = () => {
    if (!visibleRows.length) return alert('No rows to export.');
    const sheetRows = [
      ...visibleRows.map((r, i) => ({
        '#': i + 1,
        Supplier: r.supplier_name,
        Currency: r.currency,
        'Pending Bills': r.pending_bills,
        'Pending Total': r.pending_total,
        '< 30 Days': r.b_30,
        '30-60 Days': r.b_30_60,
        '60-120 Days': r.b_60_120,
        '> 120 Days': r.b_120_plus,
        'Oldest PO': r.oldest_po_date,
      })),
      ...(data.totals_by_currency || []).map((t) => ({
        '#': '', Supplier: `TOTAL (${t.currency || '—'})`, Currency: t.currency,
        'Pending Bills': t.pending_bills,
        'Pending Total': t.pending_total,
        '< 30 Days': t.b_30, '30-60 Days': t.b_30_60,
        '60-120 Days': t.b_60_120, '> 120 Days': t.b_120_plus,
        'Oldest PO': '',
      })),
    ];
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(sheetRows);
    XLSX.utils.book_append_sheet(wb, ws, 'Creditors Aging');
    XLSX.writeFile(wb, `creditors_aging_${data.as_of || new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  /* ── Letterhead-styled PDF (hidden iframe) ──────────────────────── */
  const exportPDF = () => {
    if (!visibleRows.length) return alert('No rows to export.');
    const bodyRows = visibleRows.map((r, i) => `
      <tr>
        <td>${i + 1}</td>
        <td>${(r.supplier_name || '').replace(/</g, '&lt;')}</td>
        <td>${r.currency || ''}</td>
        <td class="r">${r.pending_bills}</td>
        <td class="r">${fmt(r.pending_total)}</td>
        <td class="r">${fmt(r.b_30)}</td>
        <td class="r">${fmt(r.b_30_60)}</td>
        <td class="r">${fmt(r.b_60_120)}</td>
        <td class="r">${fmt(r.b_120_plus)}</td>
        <td>${r.oldest_po_date || ''}</td>
      </tr>`).join('');

    const totalsRows = (data.totals_by_currency || []).map((t) => `
      <tr class="totals">
        <td colspan="2"><b>TOTAL (${t.currency || '—'})</b></td>
        <td>${t.currency}</td>
        <td class="r"><b>${t.pending_bills}</b></td>
        <td class="r"><b>${fmt(t.pending_total)}</b></td>
        <td class="r"><b>${fmt(t.b_30)}</b></td>
        <td class="r"><b>${fmt(t.b_30_60)}</b></td>
        <td class="r"><b>${fmt(t.b_60_120)}</b></td>
        <td class="r"><b>${fmt(t.b_120_plus)}</b></td>
        <td></td>
      </tr>`).join('');

    const headerHtml = `
      <div class="lh">
        ${companySettings?.logo_url ? `<img class="lh-logo" src="${companySettings.logo_url}" alt="" />` : ''}
        <div class="lh-meta">
          <div class="lh-name">${(companySettings?.name || '').replace(/</g, '&lt;')}</div>
          ${companySettings?.tax_id ? `<div class="lh-line">VAT: ${companySettings.tax_id}</div>` : ''}
        </div>
        ${companySettings?.document_header_url ? `<img class="lh-banner" src="${companySettings.document_header_url}" alt="" />` : ''}
      </div>`;

    const html = `
      <!doctype html><html><head><title>Creditors Aging — ${data.as_of || ''}</title>
      <style>
        * { box-sizing: border-box; }
        body { margin: 0; font-family: 'Segoe UI', Arial, sans-serif; color: #111; }
        .page { padding: 12mm 10mm; }
        .lh { display: flex; align-items: center; gap: 12px; border-bottom: 2px solid #0f766e; padding-bottom: 8px; margin-bottom: 10px; }
        .lh-logo { max-height: 56px; max-width: 130px; object-fit: contain; }
        .lh-banner { max-height: 56px; max-width: 240px; margin-left: auto; object-fit: contain; }
        .lh-name { font-size: 16pt; font-weight: 800; color: #0f172a; }
        .lh-line { font-size: 9pt; color: #64748b; }
        .doc-title { font-size: 14pt; font-weight: 700; color: #0f172a; margin: 6px 0; }
        .subline { font-size: 9pt; color: #475569; margin-bottom: 8px; }
        table { width: 100%; border-collapse: collapse; font-size: 9pt; }
        th, td { border: 1px solid #cbd5e1; padding: 5px 7px; text-align: left; }
        th { background: #f1f5f9; font-size: 8pt; color: #475569; text-transform: uppercase; letter-spacing: 0.4px; }
        td.r, th.r { text-align: right; font-variant-numeric: tabular-nums; }
        tr.totals td { background: #ecfeff; font-size: 10pt; }
        .meta { font-size: 8pt; color: #94a3b8; margin-top: 10px; text-align: right; }
        @media print { @page { size: A4 landscape; margin: 8mm; } }
      </style></head>
      <body>
        <div class="page">
          ${headerHtml}
          <div class="doc-title">Creditors Aging — Sundry Creditors (Age Wise)</div>
          <div class="subline">As of: <b>${data.as_of}</b> · Only approved / received POs are counted.</div>
          <table>
            <thead>
              <tr>
                <th>#</th><th>Supplier</th><th>Ccy</th>
                <th class="r">Pending Bills</th><th class="r">Pending Total</th>
                <th class="r">&lt; 30 days</th><th class="r">30-60 days</th>
                <th class="r">60-120 days</th><th class="r">&gt; 120 days</th>
                <th>Oldest PO</th>
              </tr>
            </thead>
            <tbody>${bodyRows}${totalsRows}</tbody>
          </table>
          <div class="meta">Generated ${new Date().toLocaleString()}</div>
        </div>
      </body></html>`;

    const iframe = document.createElement('iframe');
    Object.assign(iframe.style, { position: 'fixed', right: 0, bottom: 0, width: 0, height: 0, border: 0 });
    document.body.appendChild(iframe);
    const doc = iframe.contentDocument;
    doc.open(); doc.write(html); doc.close();
    setTimeout(() => {
      const imgs = Array.from(doc.images || []);
      const waiters = imgs.map((img) => (img.complete && img.naturalWidth > 0)
        ? Promise.resolve()
        : new Promise((res) => {
            img.addEventListener('load', res, { once: true });
            img.addEventListener('error', res, { once: true });
            setTimeout(res, 3000);
          }));
      Promise.all(waiters).then(() => {
        try { iframe.contentWindow.focus(); iframe.contentWindow.print(); } catch { /* noop */ }
        setTimeout(() => { try { document.body.removeChild(iframe); } catch { /* noop */ } }, 1500);
      });
    }, 200);
  };

  if (loading) return <div className="bg-white border rounded-lg p-6 text-center text-sm text-slate-500">Loading…</div>;
  if (!data) return <div className="bg-white border rounded-lg p-6 text-center text-sm text-slate-500">No data</div>;

  return (
    <div className="space-y-4" data-testid="creditors-aging-tab">
      <div className="flex flex-wrap items-end justify-between gap-3 bg-white border rounded-lg p-3">
        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">As of</label>
            <input
              type="date"
              value={asOf || data.as_of || ''}
              onChange={(e) => setAsOf(e.target.value)}
              className="border rounded px-2 py-1.5 text-sm"
              data-testid="ca-as-of"
            />
          </div>
          <div className="min-w-[220px]">
            <label className="block text-xs font-semibold text-slate-700 mb-1">Search supplier</label>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Type to filter…"
              className="border rounded px-2 py-1.5 text-sm w-full"
              data-testid="ca-search"
            />
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={exportExcel} disabled={!visibleRows.length} className="px-3 py-1.5 rounded border border-emerald-600 text-emerald-700 hover:bg-emerald-50 text-sm disabled:opacity-40" data-testid="ca-excel">📊 Excel</button>
          <button onClick={exportPDF} disabled={!visibleRows.length} className="px-3 py-1.5 rounded border border-rose-600 text-rose-700 hover:bg-rose-50 text-sm disabled:opacity-40" data-testid="ca-pdf">📄 PDF / Print</button>
        </div>
      </div>

      {/* Per-currency totals strip */}
      {(data.totals_by_currency || []).length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {(data.totals_by_currency || []).map((t) => (
            <div key={t.currency || '_'} className="border rounded-lg p-3 bg-amber-50 border-amber-200 text-amber-900" data-testid={`ca-tot-${t.currency || 'none'}`}>
              <div className="text-[10px] uppercase tracking-wide opacity-70">Total payable · {t.currency || '—'}</div>
              <div className="text-lg font-mono font-bold">{fmt(t.pending_total)} {t.currency || ''}</div>
              <div className="text-[11px] text-amber-700 mt-1">
                {t.pending_bills} bill{t.pending_bills === 1 ? '' : 's'} ·
                {' <30: '}<b>{fmt(t.b_30)}</b> ·
                {' 30-60: '}<b>{fmt(t.b_30_60)}</b> ·
                {' 60-120: '}<b>{fmt(t.b_60_120)}</b> ·
                {' >120: '}<b>{fmt(t.b_120_plus)}</b>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="bg-white border rounded-lg overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-3 py-2 text-left">#</th>
              <th className="px-3 py-2 text-left">Supplier</th>
              <th className="px-3 py-2 text-left">Ccy</th>
              <th className="px-3 py-2 text-right">Pending Bills</th>
              <th className="px-3 py-2 text-right">Pending Total</th>
              <th className="px-3 py-2 text-right">&lt; 30 Days</th>
              <th className="px-3 py-2 text-right">30-60 Days</th>
              <th className="px-3 py-2 text-right">60-120 Days</th>
              <th className="px-3 py-2 text-right">&gt; 120 Days</th>
              <th className="px-3 py-2 text-left">Oldest PO</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((r, i) => (
              <tr key={`${r.supplier_id}_${r.currency}`} className="border-t hover:bg-slate-50" data-testid={`ca-row-${r.supplier_id}-${r.currency || 'none'}`}>
                <td className="px-3 py-2 text-slate-500">{i + 1}</td>
                <td className="px-3 py-2 font-medium">
                  {r.supplier_name}
                  {(r.unapplied_advance || 0) > 0 && (
                    <span className="ml-2 inline-block text-[10px] px-1.5 py-0.5 rounded bg-indigo-50 border border-indigo-200 text-indigo-700" title={`Net of ${fmt(r.unapplied_advance)} ${r.currency || ''} unapplied advance`}>
                      −{fmt(r.unapplied_advance)} adv
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-xs font-mono">{r.currency || '—'}</td>
                <td className="px-3 py-2 text-right">{r.pending_bills}</td>
                <td className="px-3 py-2 text-right font-mono font-semibold">{fmt(r.pending_total)}</td>
                <td className="px-3 py-2 text-right font-mono text-emerald-700">{r.b_30 ? fmt(r.b_30) : '-'}</td>
                <td className="px-3 py-2 text-right font-mono text-amber-700">{r.b_30_60 ? fmt(r.b_30_60) : '-'}</td>
                <td className="px-3 py-2 text-right font-mono text-orange-700">{r.b_60_120 ? fmt(r.b_60_120) : '-'}</td>
                <td className="px-3 py-2 text-right font-mono text-rose-700">{r.b_120_plus ? fmt(r.b_120_plus) : '-'}</td>
                <td className="px-3 py-2 text-xs text-slate-500">{r.oldest_po_date || '-'}</td>
              </tr>
            ))}
            {!visibleRows.length && (
              <tr><td colSpan={10} className="px-3 py-6 text-center text-slate-500">No outstanding creditors. (Drafts &amp; unapproved POs are excluded.)</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="text-xs text-slate-500">
        Showing {visibleRows.length} of {data.rows.length} creditor row{data.rows.length === 1 ? '' : 's'} · Aging based on po_date · Only approved / received POs counted · As of {data.as_of}
      </div>
    </div>
  );
};

export default CreditorsAgingTab;
